/**
 * Screens of two course exports matched by their toc names - the one rule behind the
 * comparator's screen pairs and the app header's tabs between two exports, or between a Bronte
 * export and its EPUB.
 *
 * Every screen is keyed on its toc path: chapter > lesson > screen in a 3-level toc,
 * chapter > screen in a 2-level one (and an EPUB toc, one page per lesson, is 2-level). A path
 * level that repeats its parent is dropped, so the opening screen of a 3-level lesson
 * ("1.3 Units > 1.3 Units") keys like the 2-level screen holding that whole lesson. The two
 * path sequences are then aligned in toc order:
 *
 *   exact   same path (longest common subsequence, which keeps recurring names such as
 *           "Important Points" matched to the occurrence in the same position)
 *   prefix  one path starts with the other, between the same exact neighbours: a 2-level
 *           screen covering a 3-level lesson, an EPUB page covering a lesson's screens, a
 *           screen renamed within its lesson ("Index > B > B" vs "Index > B > Index B")
 *   near    navigation only: the partner of the nearest matched screen before (else after)
 *
 * Exact matches and the first prefix match of each screen are one-to-one pairs, the screens
 * the comparator compares; the navigation maps give every screen a destination.
 *
 * Plain JavaScript with only `export function` declarations and no imports, like
 * appHeaderLinks.js: the browser apps import it as an ES module, the comparator (CommonJS on
 * Node 18) loads it through toc.js.
 */

/**
 * @typedef {{ screenId: string, title: string, playOrder: number, chapter: string, lesson: string, stitched?: boolean }} TocScreen
 *   stitched: a stitched lesson - a playOrder ACTIVITY routed as one page of its child screens
 * @typedef {"exact" | "prefix" | "near"} MatchKind
 * @typedef {{
 *   pairs: { index1: number, index2: number, kind: "exact" | "prefix" }[],
 *   of1: { index: number, kind: MatchKind }[],
 *   of2: { index: number, kind: MatchKind }[],
 * }} ScreenMatch
 *   `pairs` one-to-one, in side-1 order; `of1[i]` / `of2[j]` the other side's screen a
 *   side-1 / side-2 screen navigates to (index -1 when the other side has no screens)
 */

// EPUB toc: chapter nodes holding module nodes, each pointing at
// OPS/xhtml/<screenId>.xhtml (the file name is the screen id epub-reader routes
// by). A chapter's own node shares the page of its first module, so only leaf
// nodes become screens, one per xhtml file, in toc order.
function listEpubScreens(tocRoot) {
  const screens = [];
  const seen = new Set();
  const walk = (node, ancestors) => {
    const children = node.children || [];
    if (!children.length && node.uri) {
      const screenId = node.uri
        .split("/")
        .pop()
        .replace(/\.[^/.]+$/, "");
      if (!seen.has(screenId)) {
        seen.add(screenId);
        screens.push({
          screenId,
          title: node.title || "",
          playOrder: screens.length + 1,
          chapter: (ancestors[1] && ancestors[1].title) || "",
          lesson: (ancestors[2] && ancestors[2].title) || "",
        });
      }
    }
    children.forEach((child) => walk(child, [...ancestors, node]));
  };
  walk(tocRoot, []);
  return screens;
}

/**
 * The routable screens of a toc.json, in play order. Bronte: the nodes carrying a playOrder
 * (for stitched/lab lessons it sits on the lesson ACTIVITY rather than its child screens, so
 * screens are collected by playOrder presence, not by leaf type); exports without any playOrder
 * (re-exported/duplicated books) route by their INTERACTIVE nodes, in toc order. EPUB: one
 * screen per xhtml page.
 * @param {object} tocRoot
 * @param {{ epub?: boolean }} [options]
 * @returns {TocScreen[]}
 */
export function listTocScreens(tocRoot, options) {
  if (options && options.epub) {
    return listEpubScreens(tocRoot);
  }
  let hasPlayOrder = false;
  const scan = (node) => {
    if (node.playOrder !== undefined && node.playOrder !== null) hasPlayOrder = true;
    (node.children || []).forEach(scan);
  };
  scan(tocRoot);
  let sequence = 0;
  const isScreen = (node) =>
    hasPlayOrder
      ? node.playOrder !== undefined && node.playOrder !== null
      : node.type === "INTERACTIVE";
  const screens = [];
  const walk = (node, ancestors) => {
    if (isScreen(node)) {
      if (!hasPlayOrder) sequence++;
      screens.push({
        screenId: node.id,
        title: node.title || "",
        playOrder: hasPlayOrder ? node.playOrder : sequence,
        // ancestors[0] is the Book root; below it sit chapter then lesson ACTIVITY nodes
        chapter: (ancestors[1] && ancestors[1].title) || "",
        lesson: (ancestors[2] && ancestors[2].title) || "",
      });
      // a stitched lesson: the playOrder sits on an ACTIVITY with child screens, which has no
      // screen of its own (no screens/<id>.json) - Express cannot open it and BV never
      // completes its route, so it is not compared
      if (node.type !== "INTERACTIVE" && (node.children || []).length) {
        screens[screens.length - 1].stitched = true;
      }
    }
    (node.children || []).forEach((child) => walk(child, [...ancestors, node]));
  };
  walk(tocRoot, []);
  screens.sort((a, b) => a.playOrder - b.playOrder);
  return screens;
}

function normalizeName(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[‘’“”]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * A screen's toc path, normalized: chapter, lesson (3-level tocs), screen title, without empty
 * levels or a level repeating its parent.
 * @param {TocScreen} screen
 * @returns {string[]}
 */
export function screenPath(screen) {
  const path = [];
  [screen.chapter, screen.lesson, screen.title].forEach((name) => {
    const level = normalizeName(name);
    if (level && level !== path[path.length - 1]) {
      path.push(level);
    }
  });
  return path;
}

// Standard LCS over two key arrays; returns [i, j] index pairs in order.
function alignByLcs(keysA, keysB) {
  const rows = keysA.length;
  const cols = keysB.length;
  const width = cols + 1;
  const table = new Uint16Array((rows + 1) * width);
  for (let i = rows - 1; i >= 0; i--) {
    for (let j = cols - 1; j >= 0; j--) {
      table[i * width + j] =
        keysA[i] === keysB[j]
          ? table[(i + 1) * width + j + 1] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
    }
  }
  const pairs = [];
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (keysA[i] === keysB[j]) {
      pairs.push([i, j]);
      i++;
      j++;
    } else if (table[(i + 1) * width + j] >= table[i * width + j + 1]) {
      i++;
    } else {
      j++;
    }
  }
  return pairs;
}

// one path is the start of the other (and they differ)
function nested(pathA, pathB) {
  const [shorter, longer] = pathA.length < pathB.length ? [pathA, pathB] : [pathB, pathA];
  return (
    shorter.length < longer.length &&
    shorter.length > 0 &&
    shorter.every((level, k) => level === longer[k])
  );
}

/**
 * Prefix matches of one side's unmatched screens: each looks between the other-side partners of
 * its nearest exact neighbours (the one before included) for the first screen whose path nests
 * with its own.
 * @returns {Map<number, number>} unmatched index -> other side's index
 */
function prefixMatches(paths, otherPaths, exactOf) {
  const found = new Map();
  let before = -1; // other-side partner of the last exact match so far
  for (let i = 0; i < paths.length; i++) {
    if (exactOf[i] !== -1) {
      before = exactOf[i];
      continue;
    }
    let after = otherPaths.length;
    for (let k = i + 1; k < paths.length; k++) {
      if (exactOf[k] !== -1) {
        after = exactOf[k];
        break;
      }
    }
    for (let j = Math.max(before, 0); j < after; j++) {
      if (nested(paths[i], otherPaths[j])) {
        found.set(i, j);
        break;
      }
    }
  }
  return found;
}

// navigation for screens matched neither way: the partner of the nearest matched screen
// before, else after
function fillNear(of, length) {
  for (let i = 0; i < length; i++) {
    if (of[i]) continue;
    let near = -1;
    for (let k = i - 1; k >= 0 && near === -1; k--) {
      if (of[k] && of[k].kind !== "near") near = of[k].index;
    }
    for (let k = i + 1; k < length && near === -1; k++) {
      if (of[k] && of[k].kind !== "near") near = of[k].index;
    }
    of[i] = { index: near, kind: "near" };
  }
}

/**
 * Matches two screen lists (listTocScreens output) by toc path - see the file comment.
 * @param {TocScreen[]} screens1
 * @param {TocScreen[]} screens2
 * @returns {ScreenMatch}
 */
export function matchScreenLists(screens1, screens2) {
  const paths1 = screens1.map(screenPath);
  const paths2 = screens2.map(screenPath);
  const exact = alignByLcs(
    paths1.map((path) => path.join("\u0001")),
    paths2.map((path) => path.join("\u0001")),
  );
  const exactOf1 = screens1.map(() => -1);
  const exactOf2 = screens2.map(() => -1);
  exact.forEach(([i, j]) => {
    exactOf1[i] = j;
    exactOf2[j] = i;
  });

  /** @type {ScreenMatch["of1"]} */
  const of1 = new Array(screens1.length);
  /** @type {ScreenMatch["of2"]} */
  const of2 = new Array(screens2.length);
  exact.forEach(([i, j]) => {
    of1[i] = { index: j, kind: "exact" };
    of2[j] = { index: i, kind: "exact" };
  });

  const paired1 = new Set(exact.map(([i]) => i));
  const paired2 = new Set(exact.map(([, j]) => j));
  const pairs = exact.map(([i, j]) => ({ index1: i, index2: j, kind: "exact" }));
  const addPrefixPair = (i, j) => {
    if (!paired1.has(i) && !paired2.has(j)) {
      paired1.add(i);
      paired2.add(j);
      pairs.push({ index1: i, index2: j, kind: "prefix" });
    }
  };
  prefixMatches(paths1, paths2, exactOf1).forEach((j, i) => {
    of1[i] = { index: j, kind: "prefix" };
    addPrefixPair(i, j);
  });
  prefixMatches(paths2, paths1, exactOf2).forEach((i, j) => {
    of2[j] = { index: i, kind: "prefix" };
    addPrefixPair(i, j);
  });
  pairs.sort((a, b) => a.index1 - b.index1);

  fillNear(of1, screens1.length);
  fillNear(of2, screens2.length);
  return { pairs, of1, of2 };
}

/**
 * The screen of the other side a screen navigates to, or undefined when either side has none.
 * @param {TocScreen[]} screens1
 * @param {TocScreen[]} screens2
 * @param {ScreenMatch} match  matchScreenLists(screens1, screens2)
 * @param {string} screenId  a screen of side `fromSide`
 * @param {1 | 2} fromSide
 * @returns {string | undefined}
 */
export function counterpartScreenId(screens1, screens2, match, screenId, fromSide) {
  const [own, other, of] =
    fromSide === 1 ? [screens1, screens2, match.of1] : [screens2, screens1, match.of2];
  const index = own.findIndex((screen) => screen.screenId === screenId);
  const target = index === -1 ? undefined : of[index];
  return target && target.index !== -1 ? other[target.index].screenId : undefined;
}
